import { Components } from "appworks/components/components";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Text } from "appworks/graphics/pixi/text";
import { gameState } from "appworks/model/game-state";
import { commsManager } from "appworks/server/comms-manager";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { fade, fadeIn, fadeOut } from "appworks/utils/animation/fade";
import { fadeOut2 } from "appworks/utils/animation/fade2";
import { scale, scaleIn, scaleOut } from "appworks/utils/animation/scale";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { RandomRangeInt } from "appworks/utils/math/random";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { FowlSlotBonusResult } from "model/results/fowl-slot-bonus-result";
import { SignalBinding } from "signals";
import { SlingoLadderResult } from "slingo/model/results/slingo-ladder-result";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { FreespinRequestPayload } from "slotworks/model/gameplay/requests/freespin-request-payload";
import { SpinState } from "slotworks/state-machine/standard/states/spin-state";
import { ApplyFowlPlayTextStyling } from "util/fowl-text-style";

export class FowlSlotSpinState extends SpinState {
    protected anticipationBindings: SignalBinding[] = [];

    public onEnter(): void {
        uiFlags.set(UIFlag.IDLE, false);
        uiFlags.set(UIFlag.NO_SKIP, true);
        uiFlags.set(UIFlag.SPINNING, true);

        const matrix = Components.get(MatrixComponent);
        const anticipationHighlight = gameLayers.MatrixBackground.getShape("anticipation_highlight");

        this.anticipationBindings.push(
            matrix.onAnticipationReel.add(reelIndex => {
                const pos = gameLayers.MatrixContent.getPosition(`symbol_${reelIndex}_0`);

                anticipationHighlight.landscape.x = pos.landscape.x + pos.landscape.width / 2 - anticipationHighlight.landscape.width / 2;
                anticipationHighlight.portrait.x = pos.portrait.x + pos.portrait.width / 2 - anticipationHighlight.portrait.width / 2;
                anticipationHighlight.visible = true;
            }),
            matrix.onAnticipationEnd.add(() => {
                anticipationHighlight.visible = false;
            })
        );

        new Sequence([
            () => this.makeFakeRequest(),
            () =>
                Contract.wrap(() => {
                    const spinsLeftValue = gameLayers.BonusUI.getText("spins_remaining_value");
                    const result = gameState.getCurrentGame().getLatestResultOfType(FowlSlotBonusResult);
                    spinsLeftValue.text = (result.totalSpins - gameState.getCurrentGame().getFreespinsPlayed()).toString();
                }),
            () => Contract.wrap(() => Services.get(SoundService).customEvent("slot_reelspin")),
            () => matrix.startTransition(),
            () => this.wildsAddedSequenceOrSpinDelay(),
        ]).then(() => super.onEnter());
    }

    public onExit(): void {
        this.anticipationBindings.forEach(binding => binding.detach());
        this.anticipationBindings.length = 0;

        super.onExit();
    }

    protected makeFakeRequest(): Contract {
        return new Sequence([
            () => commsManager.request(new FreespinRequestPayload()),
            () => Contract.wrap(() => gameState.getCurrentGame().setToLatestRecord()),
        ]);
    }

    protected wildsAddedSequenceOrSpinDelay(): Contract {
        const totalDuration = 8000;
        const wildDropDuration = 500;
        const gameplay = gameState.getCurrentGame();

        let wildsAdded = 0;
        const ladderResult = gameplay.getLatestResultOfType(SlingoLadderResult);
        if (ladderResult.total === 8) wildsAdded = 120;
        if (ladderResult.total === 10) wildsAdded = 240;

        if (gameplay.getFreespinsPlayed() === 1 && wildsAdded > 0) {
            let text: Text;
            let fox: Sprite;

            const wildDrops: Array<() => Contract> = [];
            for (let i = 0; i < wildsAdded / 3; i++) {
                wildDrops.push(() =>
                    Contract.getDelayedContract(RandomRangeInt(1500, totalDuration - wildDropDuration), () =>
                        this.dropWildIntoReel(RandomRangeInt(0, 4), wildDropDuration)
                    )
                );
            }

            return new Sequence([
                () => gameLayers.CelebrationContent.setScene("wilds_added"),
                () =>
                    new Parallel([
                        () => Contract.getTimeoutContract(totalDuration),
                        () =>
                            new Sequence([
                                () =>
                                    Contract.wrap(() => {
                                        text = gameLayers.CelebrationContent.getText("value");
                                        ApplyFowlPlayTextStyling(text, 3);
                                        text.landscape.scale.set(0);
                                        text.portrait.scale.set(0);

                                        fox = gameLayers.CelebrationContent.getSprite("fox");
                                        fox.landscape.scale.set(0);
                                        fox.portrait.scale.set(0);

                                        Services.get(SoundService).customEvent("slot_wilds_added_fox");
                                    }),
                                () => scale(fox, { x: 1.5, y: 1.5 }, 1000, Easing.Back.Out),
                                () => Contract.getTimeoutContract(500),
                                () =>
                                    Contract.wrap(() => {
                                        fadeOut2(fox, 500, Easing.Sinusoidal.Out).execute();
                                    }),
                                () => {
                                    const wilds = { value: 0 };
                                    const tween = new Tween(wilds)
                                        .to({ value: wildsAdded }, totalDuration * 0.7)
                                        .easing(Easing.Sinusoidal.Out)
                                        .onUpdate(
                                            () => (text.text = Services.get(TranslationsService).get("wilds_added_caps", { value: Math.round(wilds.value) }))
                                        )
                                        .onStart(() => Services.get(SoundService).customEvent("slot_wilds_added_start"))
                                        .onComplete(() => Services.get(SoundService).customEvent("slot_wilds_added_end"));

                                    return new Parallel([() => TweenContract.wrapTween(tween), () => scaleIn(text, 500, Easing.Back.Out)]);
                                },
                            ]),
                        () => fade(gameLayers.MatrixContent.container, 1, 0.5, 500),
                        ...wildDrops,
                    ]),
                () => new Parallel([() => scaleOut(text, 500, Easing.Back.In), () => fade(gameLayers.MatrixContent.container, 0.5, 1, 500)]),
                () => gameLayers.CelebrationContent.defaultScene(),
            ]);
        } else {
            return Contract.getTimeoutContract(500);
        }
    }

    protected dropWildIntoReel(reelIndex: number, duration): Contract {
        const wild = Services.get(GraphicsService).createSprite("slotbonus_WI");
        gameLayers.MatrixForeground.add(wild);
        wild.alpha = 0;

        const startPosition = gameLayers.MatrixContent.getPosition(`symbol_${reelIndex}_0`);
        startPosition.landscape.y -= startPosition.landscape.height * 3;
        startPosition.portrait.y -= startPosition.portrait.height * 3;
        wild.setDualPosition(startPosition);

        CenterPivot(wild);
        wild.landscape.scale.set(1.5); // because I'm too lazy to do proper setting of static pos, offsetting etc etc
        wild.portrait.scale.set(1.5);

        const endPosition = gameLayers.MatrixContent.getPosition(`symbol_${reelIndex}_2`);

        const tweenLandscape = new Tween(wild.landscape).to({ y: endPosition.landscape.y }, duration);
        const tweenPortrait = new Tween(wild.portrait).to({ y: endPosition.portrait.y }, duration).onComplete(() => wild.destroy());

        return new Parallel([
            () => new Sequence([() => fadeIn(wild, duration / 3), () => Contract.getTimeoutContract(duration / 3), () => fadeOut(wild, duration / 3)]),
            () => TweenContract.wrapTween(tweenLandscape),
            () => TweenContract.wrapTween(tweenPortrait),
        ]);
    }

    protected attachLandSounds(): void {
        const matrix = Components.get(MatrixComponent);
        const sound = Services.get(SoundService);
        this.signals = [matrix.landSignal.add(reelIndex => sound.customEvent("slot_reelstop"))];
    }
}
